import React, { useState, useEffect } from 'react';
import { Layout } from './CoreComponents';
import ItemView from './Shared/ItemView';
import { formatPrice, formatAmount, formatQuantity } from '../utils/formatters';
import { useToast } from '../contexts/ToastContext';
import { useDispatch, useSelector } from 'react-redux';
import { getPORequestAwardedSuppliersList, getPORequestSupplierAwardedQuoteItemsList, clearAwardedSuppliers, clearSupplierAwardedQuoteItems } from '../store/slices/poRequestSlice';
import type { AppDispatch, RootState } from '../store/index';

interface CartItem {
  id: string;
  itemId?: number; // ItemView modal için gerekli
  productName: string;
  productCode: string;
  category: string;
  supplier: string;
  requestId: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  deliveryTime: string;
  addedDate: string;
  icon: string;
  vatRate: number; // KDV oranı
  vatIncluded: boolean; // KDV dahil mi
  currency: string; // Para birimi
}

interface PurchaseOrderPageProps {}

const PurchaseOrderPage: React.FC<PurchaseOrderPageProps> = () => {
  const { showSuccess, showWarning } = useToast();
  const dispatch = useDispatch<AppDispatch>();
  
  // Redux state
  const { loading: suppliersLoading, loading: itemsLoading } = useSelector((state: RootState) => state.poRequest);
  const [awardedSuppliers, setAwardedSuppliers] = useState<any[]>([]);
  
  // Local state
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showItemViewModal, setShowItemViewModal] = useState(false);
  const [itemToView, setItemToView] = useState<any>(null);

  // Fetch awarded suppliers on component mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // Clear existing data to ensure fresh fetch
      dispatch(clearAwardedSuppliers());
      dispatch(clearSupplierAwardedQuoteItems());
      
      // Fetch fresh data with cache busting
      dispatch(getPORequestAwardedSuppliersList(true)).then((result) => {
        if (result.type.endsWith('/fulfilled')) {
          // Response'dan tedarikçi listesini çıkar
          const responseData = result.payload;
          if (Array.isArray(responseData)) {
            setAwardedSuppliers(responseData);
          } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
            const data = (responseData as any).data;
            if (Array.isArray(data)) {
              setAwardedSuppliers(data);
            } else if (data && data.suppliers) {
              setAwardedSuppliers(data.suppliers);
            }
          }
        }
      });
    }
  }, [dispatch]);

  // Tedarikçi değiştiğinde tüm ürünleri otomatik seç
  useEffect(() => {
    if (cartItems.length > 0 && selectedSupplierId) {
      // Tüm ürünleri otomatik seç
      setSelectedItems(new Set(cartItems.map(item => item.id)));
    }
  }, [cartItems, selectedSupplierId]);




  // Map API response to CartItem
  const mapApiResponseToCartItem = (apiItem: any, supplierName: string): CartItem => ({
    id: apiItem.id?.toString() || '',
    itemId: apiItem.itemId || undefined, // ItemView modal için gerekli
    productName: apiItem.itemName || '',
    productCode: apiItem.itemCode || '',
    category: apiItem.itemCategory || '', // API'den itemCategory alanını al
    supplier: supplierName,
    requestId: apiItem.poRequestId?.toString() || '',
    quantity: apiItem.offeredQuantity || 0,
    unitPrice: apiItem.offeredPrice || 0,
    totalPrice: (apiItem.offeredQuantity || 0) * (apiItem.offeredPrice || 0),
    deliveryTime: `${apiItem.offeredDeliveryDays || 0} gün`,
    addedDate: new Date().toISOString().split('T')[0] || '',
    icon: 'fas fa-box', // Varsayılan ikon
    vatRate: apiItem.vatRate || apiItem.vat_rate || 0, // KDV oranı - farklı field isimleri
    vatIncluded: apiItem.vatIncluded || apiItem.vat_included || false, // KDV dahil mi - farklı field isimleri
    currency: apiItem.currency || 'TRY' // Para birimi - varsayılan TRY
  });

  // Handle supplier selection change
  const handleSupplierChange = async (supplierId: string) => {
    setSelectedSupplierId(supplierId);
    setCartItems([]); // Clear previous items
    setSelectedItems(new Set()); // Clear selections
    
    if (supplierId) {
      try {
        // Clear existing supplier quote items to ensure fresh fetch
        dispatch(clearSupplierAwardedQuoteItems());
        
        // Fetch fresh data with cache busting
        const result = await dispatch(getPORequestSupplierAwardedQuoteItemsList({ 
          supplierId: parseInt(supplierId), 
          cacheBust: true 
        }));
        if (result.type.endsWith('/fulfilled')) {
          const payload = result.payload as any;
          const responseData = payload.response;
          
          // Seçilen tedarikçi adını bul
          const selectedSupplier = awardedSuppliers.find(s => s.id.toString() === supplierId);
          const supplierName = selectedSupplier?.supplierName || selectedSupplier?.companyName || 'Bilinmeyen Tedarikçi';
          
          if (Array.isArray(responseData)) {
            const mappedItems = responseData.map((item: any) => mapApiResponseToCartItem(item, supplierName));
            setCartItems(mappedItems);
          } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
            const data = (responseData as any).data;
            if (Array.isArray(data)) {
              const mappedItems = data.map((item: any) => mapApiResponseToCartItem(item, supplierName));
              setCartItems(mappedItems);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching supplier items:', error);
        showWarning('Tedarikçi ürünleri yüklenirken hata oluştu');
      }
    }
  };

  const proceedToApproval = () => {
    if (selectedItems.size === 0) {
      showWarning('Lütfen satın almak istediğiniz ürünleri seçin!');
      return;
    }
    showSuccess('Seçili ürünler satın alındı!');
    // Burada satın alma sürecine yönlendirme yapılabilir
  };

  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };



  const closeItemViewModal = () => {
    setShowItemViewModal(false);
    setItemToView(null);
  };



  const selectedItemsArray = cartItems.filter(item => selectedItems.has(item.id));
  
  // Ara toplam hesaplama (KDV hariç)
  const getSubtotal = () => {
    return selectedItemsArray.reduce((sum, item) => {
      const productTotal = item.unitPrice * item.quantity;
      if (item.vatIncluded) {
        // KDV dahil ise, KDV'siz tutarı hesapla
        const vatAmount = (productTotal * item.vatRate) / (100 + item.vatRate);
        return sum + (productTotal - vatAmount);
      } else {
        // KDV hariç ise, direkt toplam
        return sum + productTotal;
      }
    }, 0);
  };

  // KDV detayları hesaplama
  const getVatBreakdown = () => {
    const vatGroups: { [key: number]: number } = {};
    
    selectedItemsArray.forEach(item => {
      const productTotal = item.unitPrice * item.quantity;
      let vatAmount = 0;
      
      if (item.vatIncluded) {
        // KDV dahil ise, KDV tutarını çıkar
        vatAmount = (productTotal * item.vatRate) / (100 + item.vatRate);
      } else {
        // KDV hariç ise, KDV tutarını ekle
        vatAmount = (productTotal * item.vatRate) / 100;
      }
      
      const existingAmount = vatGroups[item.vatRate];
      vatGroups[item.vatRate] = (existingAmount ?? 0) + vatAmount;
    });
    
    return vatGroups;
  };

  // KDV toplamı
  const getVatTotal = () => {
    const vatBreakdown = getVatBreakdown();
    return Object.values(vatBreakdown).reduce((sum, amount) => sum + amount, 0);
  };

  // Genel toplam
  const getGrandTotal = () => {
    return getSubtotal() + getVatTotal();
  };

  // Loading and error states will be handled when new endpoint is implemented

  return (
    <Layout currentScreen="purchase-order">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        
        

        {/* Tedarikçi Seçimi Bölümü */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6 transition-colors duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-3 mr-4">
                <i className="fas fa-building text-green-600 text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors">Tedarikçi Seçimi</h3>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
                Tedarikçi Seçin
              </label>
              <select 
                value={selectedSupplierId}
                onChange={(e) => handleSupplierChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={suppliersLoading || itemsLoading}
              >
                <option value="">
                  {suppliersLoading ? 'Tedarikçiler yükleniyor...' : itemsLoading ? 'Ürünler yükleniyor...' : 'Tedarikçi Seçin'}
                </option>
                {awardedSuppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.supplierName || supplier.companyName || 'İsimsiz Tedarikçi'}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          
          {/* Cart Items */}
          <div className="lg:col-span-2">
            {cartItems.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4 transition-colors duration-300">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600 dark:text-gray-400 transition-colors">
                      {selectedItems.size} / {cartItems.length} ürün seçildi
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-4 max-h-[400px] sm:max-h-[600px] overflow-y-auto">
              {itemsLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center transition-colors duration-300">
                  <i className="fas fa-spinner fa-spin text-primary text-4xl mb-4"></i>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 transition-colors">
                    Ürünler Yükleniyor...
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 transition-colors">
                    Seçili tedarikçiye ait ürünler getiriliyor.
                  </p>
                </div>
              ) : cartItems.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center transition-colors duration-300">
                  <i className="fas fa-trophy text-gray-400 dark:text-gray-500 text-4xl mb-4"></i>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 transition-colors">
                    {selectedSupplierId ? 'Seçili Tedarikçiye Ait Awarded Teklif Bulunamadı' : 'Tedarikçi Seçin'}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4 transition-colors">
                    {selectedSupplierId 
                      ? `Seçili tedarikçiye ait awarded teklif bulunmuyor.`
                      : 'Ürünleri görmek için lütfen bir tedarikçi seçin.'
                    }
                  </p>
                  {selectedSupplierId && (
                    <button 
                      onClick={() => handleSupplierChange('')}
                      className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <i className="fas fa-filter mr-2"></i>
                      Filtreyi Temizle
                    </button>
                  )}
                </div>
              ) : (
                cartItems.map((item) => (
                  <div key={item.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 transition-colors duration-300">
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(item.id)}
                              onChange={() => toggleItemSelection(item.id)}
                              className="w-4 h-4 text-primary bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-primary focus:ring-2"
                            />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white transition-colors">{item.productName}</h3>
                            <div className="flex items-center space-x-4 mt-1">
                              <span className="text-xs text-gray-600 dark:text-gray-400 transition-colors">
                                Ürün Kodu: <span className="font-medium text-gray-900 dark:text-white">
                                  {item.productCode}
                                </span>
                              </span>
                              <span className="text-xs text-gray-600 dark:text-gray-400 transition-colors">
                                Talep ID: <span className="font-medium">{item.requestId}</span>
                              </span>
                              {item.category && (
                                <span className="text-xs text-gray-600 dark:text-gray-400 transition-colors">
                                  Kategori: <span className="font-medium text-green-600 dark:text-green-400">{item.category}</span>
                                </span>
                              )}
                              <span className="text-xs text-gray-600 dark:text-gray-400 transition-colors">
                                KDV: <span className={`font-medium ${item.vatIncluded ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                                  {item.vatIncluded ? 'Dahil' : 'Hariç'}(%{item.vatRate})
                                </span>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Desktop Grid */}
                      <div className="hidden sm:grid grid-cols-4 gap-4 mt-3">
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Miktar</label>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white transition-colors">{formatQuantity(item.quantity)}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Birim Fiyat</label>
                          <p className="text-sm font-medium text-gray-900 dark:text-white transition-colors">{item.currency} {formatPrice(item.unitPrice)}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Tutar</label>
                          <p className="text-sm font-bold text-green-600">{item.currency} {formatAmount(item.totalPrice)}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Teslimat</label>
                          <p className="text-sm text-gray-900 dark:text-white transition-colors">{item.deliveryTime}</p>
                        </div>
                      </div>

                      {/* Mobile Cards */}
                      <div className="sm:hidden mt-3 space-y-2">
                        <div className="flex justify-between items-center py-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Miktar:</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatQuantity(item.quantity)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Birim Fiyat:</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{item.currency} {formatPrice(item.unitPrice)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Tutar:</span>
                          <span className="text-sm font-bold text-green-600">{item.currency} {formatAmount(item.totalPrice)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Teslimat:</span>
                          <span className="text-sm text-gray-900 dark:text-white">{item.deliveryTime}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Cart Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6 sticky top-4 sm:top-6 transition-colors duration-300">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4 transition-colors">Sepet Özeti</h3>
              
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                {/* Ara Toplam */}
                <div className="flex justify-between text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  <span><i className="fas fa-calculator mr-1"></i>Ara Toplam</span>
                  <span>{selectedItemsArray.length > 0 ? selectedItemsArray[0]?.currency || 'TRY' : 'TRY'} {formatAmount(getSubtotal())}</span>
                </div>
                
                {/* KDV Detayları - Her KDV oranı için ayrı satır */}
                {Object.entries(getVatBreakdown()).map(([vatRate, amount]) => (
                  <div key={vatRate} className="flex justify-between text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    <span><i className="fas mr-1"></i>KDV (%{vatRate})</span>
                    <span>{selectedItemsArray.length > 0 ? selectedItemsArray[0]?.currency || 'TRY' : 'TRY'} {formatAmount(amount)}</span>
                  </div>
                ))}
                
                {/* Genel Toplam */}
                <div className="border-t border-gray-200 dark:border-gray-600 pt-2 sm:pt-3">
                  <div className="flex justify-between text-base sm:text-lg font-bold">
                    <span className="text-gray-900 dark:text-white transition-colors">
                      <i className="fas fa-coins mr-1"></i>Genel Toplam
                    </span>
                    <span className="text-primary">{selectedItemsArray.length > 0 ? selectedItemsArray[0]?.currency || 'TRY' : 'TRY'} {formatAmount(getGrandTotal())}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                <button 
                  onClick={proceedToApproval}
                  disabled={selectedItems.size === 0}
                  className={`w-full py-2 sm:py-3 px-3 sm:px-4 rounded-lg transition-colors font-medium text-xs sm:text-sm ${
                    selectedItems.size > 0
                      ? 'bg-primary text-white hover:bg-blue-800'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <i className="fas fa-check-circle mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">Satın Alma Siparişi Oluştur</span>
                  <span className="sm:hidden">Sipariş Oluştur</span>
                </button>
              </div>


            </div>
          </div>
        </div>

        {/* ItemView Modal */}
        {showItemViewModal && itemToView && (
          <ItemView
            isOpen={showItemViewModal}
            onClose={closeItemViewModal}
            itemId={itemToView.itemId}
            mode="view"
          />
        )}

      </main>
    </Layout>
  );
};

export default PurchaseOrderPage;